import Autocomplete from './Autocomplete';
import LocationIcon from '../icons/LocationIcon';
import logger from '../../utils/logger';
import QuestionIcon from '../icons/QuestionIcon';
import { AS_ENTERED, ERROR, HELPER_TEXT, LOCATIONS, SUGGESTED } from '../../constants/autocomplete';
import { BoxProps } from '@mui/material';
import { currentLocationStatus } from '../../constants/error';
import { FocusEvent, FocusEventHandler, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getPlacesPrediction, getUserLocationFromAddress } from '../../utils/googlePlaces';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import { setLocationStatus, setUserLocationEpicAction } from '../../redux/slice/locationSlice';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  CurrentLocationStatus,
  GooglePlacesResponse,
  LocationStatus,
  UserLocation,
} from '../../types/location';
import {
  ClearLabel,
  CurrentLocation,
  HelperText,
  InvalidAddressText,
  NoLocationText,
  StyledLocationSelectError,
} from './LocationAutocompleteStyle';
import {
  AutocompleteHelperTextPosition,
  AutocompleteOption,
  AutocompleteOptionGroup,
  AutocompleteOptionType,
  AutocompletePopupPosition,
  AutocompleteStandardOption,
  LocationHelperText,
  LocationHelperTexts,
  OnAutocompleteInputChange,
  AutocompleteSelectableOption,
} from '../../types/autocomplete';

interface LocationAutocompleteProps extends BoxProps {
  hideStartIcon?: boolean;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  helperText?: LocationHelperTexts;
  popupPosition?: AutocompletePopupPosition;
  placeholder?: string;
  inputFocusSubject?: Subject<boolean>;
  showCurrentLocationSelector?: boolean;
  onDropdownToggleFn?: (popupState: boolean) => void;
  resetCurrentLocationOnClickAway?: boolean;
  isAddressAutocomplete?: boolean;
  hideAsEntered?: boolean;
  showCoordinatesOption?: boolean;
  onCoordinatesClick?: () => void;
  onSelected?: (
    value: AutocompleteSelectableOption,
    metadata?: { userLocation?: UserLocation },
  ) => void;
  setFullAddress?: (value: UserLocation | null) => void;
  setAddress?: (value: string) => void;
  setLatitudeLongitude?: (value: string | null) => void;
  getAddress?: () => string | null | undefined;
  triggerValidations?: () => void;
  triggerAddressValidation?: () => void;
  emptyFullAddress?: () => void;
  onInputBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  onInputChange?: OnAutocompleteInputChange;
  hideHelperTextContainer?: boolean;
}

function LocationAutocomplete({
  hideStartIcon,
  startIcon,
  endIcon,
  popupPosition,
  placeholder,
  helperText,
  inputFocusSubject,
  showCurrentLocationSelector,
  onDropdownToggleFn,
  resetCurrentLocationOnClickAway,
  isAddressAutocomplete,
  hideAsEntered,
  showCoordinatesOption,
  onCoordinatesClick,
  onSelected,
  setFullAddress,
  setAddress,
  setLatitudeLongitude,
  getAddress,
  triggerValidations,
  triggerAddressValidation,
  emptyFullAddress,
  hideHelperTextContainer,
  onInputBlur,
  onInputChange,
  ...rest
}: LocationAutocompleteProps) {
  const dispatch = useAppDispatch();
  const location = useAppSelector((state: RootState) => state.location.userLocation);
  const locationStatus = useAppSelector((state: RootState) => state.location.status);
  const [value, setValue] = useState<AutocompleteStandardOption | null>(null);
  const [selectedOption, setSelectedOption] = useState<AutocompleteStandardOption | null>(null);
  const [helperTextElement, setHelperTextElement] = useState<JSX.Element | undefined>(
    helperText?.default?.element,
  );
  const [helperTextPosition, setHelperTextPosition] = useState<
    AutocompleteHelperTextPosition | undefined
  >(helperText?.default?.position);

  const [currentLocation, setCurrentLocation] = useState<CurrentLocationStatus>(
    CurrentLocationStatus.NONE,
  );

  useEffect(() => {
    const subscription = currentLocationStatus.subscribe((e) => {
      setCurrentLocation(e);
    });
    return () => subscription.unsubscribe();
  }, []);

  const clearAutocompleteSubject = useMemo(
    () =>
      new Subject<{
        nextInputValue?: string;
        nextOptions?: Promise<AutocompleteOptionGroup[]>;
      }>(),
    [],
  );

  const selectHandler = async (selectedOption: AutocompleteSelectableOption) => {
    if (
      (selectedOption as AutocompleteStandardOption) &&
      (selectedOption as AutocompleteStandardOption).ignoreDefaultSelect
    ) {
      setValue({
        id: selectedOption.id,
        label: selectedOption.label,
        type: AutocompleteOptionType.STANDARD,
      });
      onSelected && onSelected(selectedOption);

      if (setAddress) {
        setAddress(selectedOption.label);
      }
      if (setLatitudeLongitude) {
        setLatitudeLongitude(null);
      }

      if (triggerValidations) {
        triggerValidations();
      }
      return;
    } else if (isAddressAutocomplete) {
      try {
        const userLocation = await getUserLocationFromAddress(
          selectedOption.id,
          selectedOption.label,
        );
        if (!userLocation) {
          return;
        }

        if (setFullAddress) setFullAddress(userLocation);
        setValue({
          id: selectedOption.id,
          label: userLocation.street ?? '',
          type: AutocompleteOptionType.STANDARD,
        });
        setSelectedOption({
          id: selectedOption.id,
          label: userLocation.street ?? '',
          type: AutocompleteOptionType.STANDARD,
        });
        onSelected && onSelected(selectedOption, { userLocation });
        if (triggerValidations) {
          triggerValidations();
        }
      } catch (error) {
        logger('error', 'Error in LocationAutocomplete:selectHandler while finding full address');
      }
    } else {
      try {
        dispatch(
          setLocationStatus({
            status: LocationStatus.IS_LOADING,
          }),
        );
        const userLocation = await getUserLocationFromAddress(
          selectedOption.id,
          selectedOption.label,
        );
        if (!userLocation) {
          return;
        }
        dispatch(setUserLocationEpicAction({ userLocation }));
        onSelected && onSelected(selectedOption);
      } catch (error) {
        dispatch(
          setLocationStatus({
            status: LocationStatus.ERROR,
          }),
        );
      }
    }
  };

  const onCurrentLocationClick = () => {
    clearAutocompleteSubject.next({
      nextInputValue: '',
      nextOptions: getOptions(''),
    });
  };

  const updateHelperText = (locationHelperText: LocationHelperText | undefined) => {
    setHelperTextElement(locationHelperText?.element);
    setHelperTextPosition(locationHelperText?.position);
  };

  const getOptions = async (value: string) => {
    if (
      currentLocation !== CurrentLocationStatus.IS_LOADING &&
      currentLocation !== CurrentLocationStatus.BAD_CURRENT_LOCATION &&
      currentLocation !== CurrentLocationStatus.BROWSER_PERMISSION_NOT_GRANTED
    ) {
      currentLocationStatus.next(CurrentLocationStatus.NONE);
    }

    let data: AutocompleteOptionGroup[] = [];
    let res: GooglePlacesResponse[] = [];
    let groupItems: AutocompleteOption[] = [];
    let groupLabel: string = '';
    try {
      if (!value) {
        updateHelperText(helperText?.default);
      } else {
        // Getting geocoding results with a fully specified precise address suggestions for address autocomplete options
        if (isAddressAutocomplete) res = await getPlacesPrediction(value, ['address']);
        else res = await getPlacesPrediction(value);
        if (res.length > 0) {
          groupItems = res.map((e) => ({
            id: e.place_id,
            label: e.description,
            type: AutocompleteOptionType.STANDARD,
          }));
          groupLabel = isAddressAutocomplete ? SUGGESTED : LOCATIONS;
          if (!isAddressAutocomplete) {
            updateHelperText(helperText?.default);
          }
        } else {
          if (isAddressAutocomplete && hideAsEntered) {
            groupLabel = SUGGESTED;
            groupItems = [
              {
                id: uuid(),
                element: (
                  <NoLocationText>
                    <FormattedMessage id='locationDropdown.noAddressText' />
                  </NoLocationText>
                ),
                type: AutocompleteOptionType.JSX,
              },
            ];
          } else if (!isAddressAutocomplete) {
            updateHelperText(helperText?.invalidInput ?? helperText?.default);
            groupLabel = LOCATIONS;
            groupItems = [
              {
                id: uuid(),
                element: (
                  <NoLocationText>
                    <FormattedMessage id='locationDropdown.nolocationsFound' />
                  </NoLocationText>
                ),
                type: AutocompleteOptionType.JSX,
              },
            ];
          }
        }
      }
    } catch (error) {
      logger('error', 'Error fetching locations', error);
    }
    if (showCurrentLocationSelector) {
      const hasTopMargin: boolean = groupItems.length > 0;
      groupItems.push({
        id: uuid(),
        type: AutocompleteOptionType.JSX,
        element: (
          <>
            <StyledLocationSelectError />
            <CurrentLocation
              hasTopMargin={hasTopMargin}
              preOnClickHandler={onCurrentLocationClick}
            />
          </>
        ),
      });
    }
    if (groupItems.length || value) {
      const optionsGroup: AutocompleteOptionGroup = {
        id: uuid(),
        groupLabel: groupLabel,
        groupItems: groupItems,
      };
      data = [optionsGroup];
      if (res.length <= 0 && hideAsEntered && !hideHelperTextContainer) {
        const errorEntryGroup: AutocompleteOptionGroup = {
          id: uuid(),
          groupLabel: ERROR,
          groupItems: [
            {
              id: uuid(),
              element: (
                <NoLocationText onClick={onCoordinatesClick}>
                  <InvalidAddressText>
                    <FormattedMessage id='locationDropdown.invalidAddress' />
                  </InvalidAddressText>
                </NoLocationText>
              ),
              type: AutocompleteOptionType.JSX,
            },
          ],
        };
        data.unshift(errorEntryGroup);
      }
      if (isAddressAutocomplete && !hideAsEntered) {
        const manualAddressEntryGroup: AutocompleteOptionGroup = {
          id: uuid(),
          groupLabel: AS_ENTERED,
          groupItems: [
            {
              id: uuid(),
              label: value,
              type: AutocompleteOptionType.STANDARD,
              ignoreDefaultSelect: true,
            },
          ],
        };
        data.unshift(manualAddressEntryGroup);
      }
      if (isAddressAutocomplete && showCoordinatesOption) {
        const coordinatesOption: AutocompleteOptionGroup = {
          id: uuid(),
          groupLabel: HELPER_TEXT,
          groupItems: [
            {
              id: uuid(),
              element: (
                <NoLocationText onClick={onCoordinatesClick}>
                  <HelperText>
                    <QuestionIcon />
                    <FormattedMessage id='locationDropdown.noAddressFoundHelperText' />
                  </HelperText>
                </NoLocationText>
              ),
              type: AutocompleteOptionType.JSX,
            },
          ],
        };
        data.push(coordinatesOption);
      }
    }
    return data;
  };

  const initializeAddressValue = () => {
    if (isAddressAutocomplete && getAddress) {
      const addressValue = getAddress();
      if (addressValue && addressValue.trim() !== '') {
        setValue({
          id: uuid(),
          label: addressValue,
          type: AutocompleteOptionType.STANDARD,
        });

        setSelectedOption({
          id: uuid(),
          label: addressValue,
          type: AutocompleteOptionType.STANDARD,
        });
      }
    }
  };

  // Run on component mount and when getAddress changes
  useEffect(() => {
    initializeAddressValue();
  }, []);

  // This tracks changes to the location state for non-address autocompletes
  useEffect(() => {
    if (isAddressAutocomplete) {
      initializeAddressValue();
      return;
    }
    let value: AutocompleteOption | null = null;
    if (location) {
      value = {
        id: location.place_id,
        label: location.address,
        type: AutocompleteOptionType.STANDARD,
      };
    }
    setValue(value);
  }, [location, getAddress]);

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    onInputBlur && onInputBlur(e);
    
    if (!hideAsEntered) {
      return;
    }

    if (selectedOption?.label != value?.label) {
      setValue(null);
      setSelectedOption(null);
      emptyFullAddress && emptyFullAddress();
    }
  };

  return (
    <Autocomplete
      value={value}
      setValue={setValue}
      startIcon={hideStartIcon ? undefined : startIcon ?? <LocationIcon />}
      endIcon={endIcon}
      submitOnEndIconClick
      clearLabel={<ClearLabel>Change Location</ClearLabel>}
      selectHandler={selectHandler}
      getOptionsHandler={getOptions}
      isLoading={locationStatus === LocationStatus.IS_LOADING}
      popupPosition={popupPosition ?? 'absolute'}
      placeholder={placeholder ?? ''}
      inputFocusSubject={inputFocusSubject}
      helperText={helperTextElement}
      helperTextPosition={helperTextPosition}
      isCheckoutFlow={hideAsEntered}
      onDropdownToggleFn={onDropdownToggleFn}
      updateAutocompleteSubject={clearAutocompleteSubject}
      resetCurrentLocationOnClickAway={resetCurrentLocationOnClickAway}
      isAddressAutocomplete={isAddressAutocomplete}
      setAddress={setAddress}
      onBlur={handleBlur}
      triggerAddressValidation={triggerAddressValidation}
      hideHelperTextContainer={hideHelperTextContainer}
      onInputChange={onInputChange}
      {...rest}
    />
  );
}

export default LocationAutocomplete;
